import React, { ChangeEvent, KeyboardEvent, useState, memo, useCallback, forwardRef } from 'react';
import styled from 'styled-components';
import theme from '@styles/theme';
import { IoEyeSharp, IoEyeOffSharp } from 'react-icons/io5';

type StyledPasswordProps = {
	width?: number;
	height?: number;
	margin?: string;
	border?: string;
	bgColor?: string;
	name?: string;
	value?: string;
	onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
	onKeyPress?: (e: KeyboardEvent<HTMLInputElement>) => void;
	maxLength?: number;
	disabled?: boolean;
	placeholder?: string;
};

const StyledPassword = forwardRef<HTMLInputElement, StyledPasswordProps>(
	(
		{
			width,
			height = 50,
			margin,
			border = `1px solid ${theme.colors.ultraLightGrayBorder}`,
			bgColor = theme.colors.whiteColor,
			name,
			value,
			onChange,
			onKeyPress,
			maxLength = 200,
			disabled = false,
			placeholder
		},
		ref
	) => {
		const [visible, setVisible] = useState(false);

		const toggleVisible = useCallback(() => {
			setVisible(prev => !prev);
		}, []);

		return (
			<Wrapper
				$width={width}
				$height={height}
				$margin={margin}
				$border={border}
				$bgColor={bgColor}
				$disabled={disabled}>
				<Input
					ref={ref}
					name={name}
					value={value}
					type={visible ? 'text' : 'password'}
					onChange={onChange}
					onKeyPress={onKeyPress}
					maxLength={maxLength}
					disabled={disabled}
					placeholder={placeholder}
					autoComplete="off"
					aria-label="비밀번호 입력"
				/>
				<ViewButton
					type="button"
					tabIndex={-1}
					onClick={toggleVisible}
					disabled={disabled}
					aria-label={visible ? '비밀번호 숨기기' : '비밀번호 보기'}>
					{visible ? (
						<IoEyeOffSharp fontSize={18} color={theme.colors.lightGrayFontColor} />
					) : (
						<IoEyeSharp fontSize={18} color={theme.colors.lightGrayFontColor} />
					)}
				</ViewButton>
			</Wrapper>
		);
	}
);

StyledPassword.displayName = 'StyledPassword';

export default memo(StyledPassword);

const Wrapper = styled.div<{
	$width?: number;
	$height?: number;
	$margin?: string;
	$border?: string;
	$bgColor?: string;
	$disabled?: boolean;
}>`
	width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
	height: ${({ $height }) => $height}px;
	margin: ${({ $margin }) => $margin ?? 0};
	display: flex;
	align-items: center;
	padding-right: 12px;
	justify-content: space-between;
	border: ${({ $border }) => $border};
	border-radius: 4px;

	background-color: ${({ $disabled, $bgColor }) => ($disabled ? theme.colors.ultraLightGrayBorder : $bgColor)};
	transition: border-color 0.2s ease;

	&:focus-within {
		border-color: ${({ $disabled }) => ($disabled ? theme.colors.ultraLightGrayBorder : theme.colors.blackColor)};
	}
`;

const Input = styled.input`
	width: 100%;
	height: 100%;
	padding: 0 16px;
	border: none;
	border-radius: 4px;
	background-color: transparent;
	font-size: 14px;
	outline: none;

	&::placeholder {
		color: ${theme.colors.deepGrayFontColor};
	}

	&:disabled {
		cursor: not-allowed;
		color: ${theme.colors.deepGrayFontColor};
	}
`;

const ViewButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px;
	border: none;
	background: transparent;
	cursor: pointer;
	transition: opacity 0.2s ease;

	&:hover:not(:disabled) {
		opacity: 0.7;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;
